/**
 * LiquidGlassPrototype — isolated visual prototype route.
 *
 * Route: /prototypes/liquid-glass
 *
 * This page is a controlled harness for the Presence Cube + Journey
 * Vessel interaction. It is deliberately isolated from the production
 * Guide Journey and does not read or write any live data.
 *
 * Layout:
 *   • Left column   — the 3D scene (JourneyVesselStack).
 *   • Right column  — controls: Simulate Check-In, Advance Stage,
 *                     Complete Checkout, Reduced Motion toggle, and a
 *                     small state readout for engineering visibility.
 *
 * The prototype separates two state machines:
 *   • Physical (owned by useLiquidGlassTransition): resting → stable → resting.
 *   • Business (owned here): idle → arrival → in_journey → departure → idle.
 *
 * The controls emit intents; the state machines decide what happens.
 */
import React, { useState, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import JourneyVesselStack from "@/components/liquid_glass/JourneyVesselStack";
import { useLiquidGlassTransition } from "@/components/liquid_glass/LiquidGlassTransition";

const BUSINESS_STAGES = ["idle", "arrival", "in_journey", "departure"];
const STAGE_LABEL = {
  idle: "No guest",
  arrival: "Arrival",
  in_journey: "In Journey",
  departure: "Departure",
};

export default function LiquidGlassPrototype() {
  const transition = useLiquidGlassTransition();
  const [businessStage, setBusinessStage] = useState("idle");

  // Simulate Check-In: kick off the physical morph and set business
  // stage to arrival. The stack only shows content once the vessel is
  // physically solid.
  const onCheckIn = useCallback(() => {
    if (transition.phase !== "resting") return;
    transition.triggerCheckIn();
    setBusinessStage("arrival");
  }, [transition]);

  // Advance through the three business states while the vessel remains
  // the same. The vessel does *not* re-morph on stage advances — it's
  // the same slab; only the content changes.
  const onAdvance = useCallback(() => {
    setBusinessStage((prev) => {
      const idx = BUSINESS_STAGES.indexOf(prev);
      if (idx < 0 || idx >= BUSINESS_STAGES.length - 1) return prev;
      return BUSINESS_STAGES[idx + 1];
    });
  }, []);

  // Complete Checkout: reverse the physical morph and clear business.
  const onCheckout = useCallback(() => {
    if (transition.phase !== "stable") return;
    setBusinessStage("idle");
    transition.triggerCheckout();
  }, [transition]);

  const canCheckIn   = transition.phase === "resting";
  const canAdvance   = transition.phase === "stable" &&
                       businessStage !== "idle" &&
                       businessStage !== "departure";
  const canCheckout  = transition.phase === "stable" &&
                       businessStage !== "idle";

  const nextStageLabel = useMemo(() => {
    if (businessStage === "arrival") return "Advance → In Journey";
    if (businessStage === "in_journey") return "Advance → Departure";
    return "Advance stage";
  }, [businessStage]);

  return (
    <div
      style={{ minHeight: "100vh", background: "#efeae2" }}
      data-testid="liquid-glass-prototype"
    >
      <TopStrip />

      <main
        style={{
          display: "grid",
          gridTemplateColumns: "minmax(0, 1fr) minmax(300px, 340px)",
          gap: 0,
          height: "calc(100vh - 56px)",
          minHeight: 560,
        }}
      >
        <section
          style={{ position: "relative", overflow: "hidden" }}
          data-testid="scene-column"
        >
          <JourneyVesselStack
            transition={transition}
            businessStage={businessStage}
          />
        </section>

        <aside
          style={{
            padding: "28px 24px",
            borderLeft: "1px solid rgba(28,25,23,0.06)",
            background: "#fbfaf7",
            display: "flex",
            flexDirection: "column",
            gap: 20,
          }}
          data-testid="control-panel"
        >
          <div>
            <div style={eyebrow}>Prototype</div>
            <div style={{ ...serifHead, marginTop: 6 }}>Presence Cube & Journey Vessel</div>
            <p style={{ ...bodySmall, marginTop: 10 }}>
              Isolated visual prototype. No live data. No connection to Guide Journey.
            </p>
          </div>

          <div style={divider} />

          <ControlButton
            label="Simulate Check-In"
            hint="Cube softens · drop separates · vessel forms."
            disabled={!canCheckIn}
            onClick={onCheckIn}
            testid="btn-check-in"
          />

          <ControlButton
            label={nextStageLabel}
            hint="Same vessel · content changes."
            disabled={!canAdvance}
            onClick={onAdvance}
            testid="btn-advance-stage"
          />

          <ControlButton
            label="Complete Checkout"
            hint="Content clears · slab softens · drop returns."
            disabled={!canCheckout}
            onClick={onCheckout}
            testid="btn-checkout"
          />

          <div style={divider} />

          <label
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              cursor: "pointer",
              fontSize: 13,
              color: "#3f3a34",
            }}
            data-testid="reduced-motion-toggle"
          >
            <input
              type="checkbox"
              checked={transition.reducedMotion}
              onChange={(e) => transition.setReducedMotion(e.target.checked)}
              style={{ accentColor: "#3f3a34" }}
            />
            <span>Reduced motion</span>
          </label>
          <p style={{ ...bodySmall, marginTop: -12 }}>
            Vessel fades in and out without fluid movement.
          </p>

          <div style={divider} />

          <StateReadout
            phase={transition.phase}
            direction={transition.direction}
            businessStage={businessStage}
            reducedMotion={transition.reducedMotion}
          />

          <div style={{ flex: 1 }} />

          <div style={{ ...bodySmall, opacity: 0.6 }}>
            The physical layer is a state machine ({transition.phase}); the
            business layer is a separate state machine ({businessStage}).
            Two systems, one vessel.
          </div>
        </aside>
      </main>
    </div>
  );
}

function TopStrip() {
  return (
    <div
      style={{
        height: 56,
        borderBottom: "1px solid rgba(28,25,23,0.06)",
        display: "flex",
        alignItems: "center",
        padding: "0 24px",
        gap: 16,
        background: "#fbfaf7",
      }}
    >
      <Link
        to="/journey/guide"
        style={{
          color: "#3f3a34",
          textDecoration: "none",
          fontSize: 12,
          letterSpacing: "0.05em",
        }}
        data-testid="link-back-guide"
      >
        ← Guide Journey
      </Link>
      <div style={{ flex: 1 }} />
      <div style={{ ...eyebrow, opacity: 0.7 }}>SONMI Prototype</div>
    </div>
  );
}

function ControlButton({ label, hint, disabled, onClick, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      data-testid={testid}
      style={{
        width: "100%",
        textAlign: "left",
        padding: "14px 16px",
        borderRadius: 14,
        border: "1px solid rgba(28,25,23,0.09)",
        background: disabled ? "#f4f1eb" : "#ffffff",
        color: disabled ? "rgba(28,25,23,0.35)" : "#1c1917",
        cursor: disabled ? "not-allowed" : "pointer",
        transition: "background 220ms ease, transform 220ms ease, box-shadow 220ms ease",
        boxShadow: disabled ? "none" : "0 1px 2px rgba(28,25,23,0.03)",
      }}
      onMouseEnter={(e) => {
        if (disabled) return;
        e.currentTarget.style.background = "#f7f4ee";
      }}
      onMouseLeave={(e) => {
        if (disabled) return;
        e.currentTarget.style.background = "#ffffff";
      }}
    >
      <div style={{ fontSize: 14, fontWeight: 500 }}>{label}</div>
      <div style={{ fontSize: 11.5, marginTop: 4, color: disabled ? "rgba(28,25,23,0.28)" : "rgba(28,25,23,0.5)" }}>
        {hint}
      </div>
    </button>
  );
}

function StateReadout({ phase, direction, businessStage, reducedMotion }) {
  return (
    <div
      style={{
        padding: "12px 14px",
        borderRadius: 12,
        background: "#f4f1eb",
        fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
        fontSize: 11,
        color: "#3f3a34",
        lineHeight: 1.7,
      }}
      data-testid="state-readout"
    >
      <div>phase: <b>{phase}</b></div>
      <div>direction: {direction}</div>
      <div>business: <b>{STAGE_LABEL[businessStage] || businessStage}</b></div>
      <div>reduced-motion: {reducedMotion ? "on" : "off"}</div>
    </div>
  );
}

// ─── Inline style helpers (kept local so the prototype is self-contained) ───
const eyebrow = {
  fontSize: 10,
  letterSpacing: "0.18em",
  textTransform: "uppercase",
  color: "rgba(28,25,23,0.55)",
};

const serifHead = {
  fontFamily: "'Cormorant Garamond', 'Playfair Display', Georgia, serif",
  fontWeight: 400,
  fontSize: 24,
  color: "#1c1917",
  letterSpacing: "-0.005em",
};

const bodySmall = {
  fontSize: 12.5,
  color: "rgba(28,25,23,0.6)",
  lineHeight: 1.55,
};

const divider = {
  height: 1,
  background: "rgba(28,25,23,0.07)",
  width: "100%",
};
